import {Entry, Locale} from 'contentful'
import getLocales from './getLocales'
import {JsonValue, LandingPage} from './types'

export const normalizeSlashes = (slug: string): string => {
  const startingSlash = slug.startsWith('/')
  const endingSlash = slug.endsWith('/')

  if (startingSlash) {
    slug = slug.substring(1)
  }
  if (endingSlash) {
    slug = slug.substring(0, slug.length - 1)
  }

  return startingSlash || endingSlash ? normalizeSlashes(slug) : slug
}

export const normalizeLocalePrefix = (
  slug: string,
  locale: string,
  defaultLocale: string,
): string => {
  let cleanSlug = normalizeSlashes(slug)

  if (locale === defaultLocale) {
    return cleanSlug
  }

  const baseLocale = getBaseLocale(locale)

  if (cleanSlug.indexOf(`${baseLocale}/`) === -1) {
    cleanSlug = `${baseLocale}/${cleanSlug}`
  }

  return cleanSlug
}

export const getBaseLocale = (locale: string): string => locale.split('-')[0]

export const replaceStaticPaths = (layout: JsonValue): void => {
  if (layout && typeof layout === 'object') {
    if (Array.isArray(layout)) {
      layout.forEach(item => replaceStaticPaths(item))
    } else {
      for (const [field, value] of Object.entries(layout)) {
        if (field === 'src' && typeof value === 'string') {
          const srcArr = value
            .trim()
            .split('/')
            .filter(v => !!v)

          if (srcArr[0] === 'static') {
            const assetPrefix = normalizeSlashes(process.env.NEXT_PUBLIC_ASSET_PREFIX || '')

            const path = normalizeSlashes([assetPrefix, 'assets', ...srcArr.slice(1)].join('/'))

            layout[field] = path.indexOf('http') !== 0 ? `/${path}` : path
          }
        } else if (typeof value === 'object') {
          replaceStaticPaths(value)
        }
      }
    }
  }
}

export const getLocaleFromPath = async (slug: string): Promise<Locale> => {
  const slugArr = normalizeSlashes(slug).split('/')

  const allLocales = await getLocales()

  const defaultLocale = allLocales.find(l => l.default)

  return (
    allLocales.find(code => !code.default && slugArr.includes(getBaseLocale(code.code))) ||
    defaultLocale
  )
}

export const isEntryLocalized = (
  defaultEntry: Entry<LandingPage>,
  entry: Entry<LandingPage>,
  defaultLocale: string,
): boolean => {
  if (entry.sys.locale === defaultLocale) {
    return true
  }

  const {siteUrl: defaultSlug} = defaultEntry.fields

  const {siteUrl: entrySlug} = entry.fields

  return defaultSlug !== entrySlug
}

export const normalizeCanonicalUrl = (
  entry: Entry<LandingPage>,
  defaultEntry?: Entry<LandingPage>,
): string | null => {
  let entryCanonicalUrl = normalizeSlashes(entry.fields.canonicalUrl || '')

  const defaultCanonicalUrl = normalizeSlashes(defaultEntry?.fields.canonicalUrl ?? '')

  if (!entryCanonicalUrl || entryCanonicalUrl === defaultCanonicalUrl) {
    entryCanonicalUrl = normalizeSlashes(entry.fields.siteUrl)
  }

  if (!entryCanonicalUrl.startsWith('http')) {
    entryCanonicalUrl = [
      process.env.NEXT_PUBLIC_PROTO || 'https://',
      process.env.NEXT_PUBLIC_HOST || 'miro.com',
      `/${entryCanonicalUrl}/`,
    ].join('')
  }

  return entryCanonicalUrl
}
